<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';

const props = defineProps({
  modelValue: { type: String, default: '' },
  minDate: { type: String, default: '' },
  showTime: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);
const { t, locale } = useI18n();
const isOpen = ref(false);
const selectedDate = ref(null);
const viewedMonth = ref(startOfMonth(new Date()));
const hour = ref('09');
const minute = ref('00');
const timeInput = ref('09:00');
const dateLocale = computed(() =>
  locale.value === 'pt-BR' ? 'pt-BR' : locale.value || 'en'
);
const weekdays = computed(() => {
  const firstSunday = new Date(2023, 0, 1);
  return Array.from({ length: 7 }, (_, index) =>
    new Intl.DateTimeFormat(dateLocale.value, { weekday: 'short' })
      .format(new Date(firstSunday.getTime() + index * 86400000))
      .replace('.', '')
  );
});
const pad = value => String(value).padStart(2, '0');
const parseLocalDateTime = value => {
  if (!value) return null;
  const [datePart, timePart = '09:00'] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours || 0, minutes || 0);
};
const minimumDate = computed(() => parseLocalDateTime(props.minDate));
const selectedDateLabel = computed(() =>
  selectedDate.value
    ? new Intl.DateTimeFormat(dateLocale.value).format(selectedDate.value)
    : t('DATE_PICKER.CHOOSE_DATE')
);
const monthLabel = computed(() =>
  new Intl.DateTimeFormat(dateLocale.value, {
    month: 'long',
    year: 'numeric',
  }).format(viewedMonth.value)
);
const calendarDays = computed(() =>
  eachDayOfInterval({
    start: startOfWeek(startOfMonth(viewedMonth.value)),
    end: endOfWeek(endOfMonth(viewedMonth.value)),
  })
);
const isDisabled = date =>
  minimumDate.value &&
  isBefore(startOfDay(date), startOfDay(minimumDate.value));
const emitValue = () => {
  if (!selectedDate.value) return;
  const dateValue = `${selectedDate.value.getFullYear()}-${pad(selectedDate.value.getMonth() + 1)}-${pad(selectedDate.value.getDate())}`;
  emit(
    'update:modelValue',
    props.showTime ? `${dateValue}T${hour.value}:${minute.value}` : dateValue
  );
};
const selectDate = date => {
  if (isDisabled(date)) return;
  selectedDate.value = date;
  viewedMonth.value = startOfMonth(date);
  emitValue();
  isOpen.value = false;
};
const clear = () => {
  selectedDate.value = null;
  emit('update:modelValue', '');
  isOpen.value = false;
};
const commitTime = () => {
  const match = timeInput.value.trim().match(/^(\d{1,2}):(\d{2})$/);
  const hours = Number(match?.[1]);
  const minutes = Number(match?.[2]);

  if (!match || hours > 23 || minutes > 59) {
    timeInput.value = `${hour.value}:${minute.value}`;
    return;
  }

  hour.value = pad(hours);
  minute.value = pad(minutes);
  timeInput.value = `${hour.value}:${minute.value}`;
  emitValue();
};
const formatTimeInput = event => {
  const digits = event.target.value.replace(/\D/g, '').slice(0, 4);
  timeInput.value =
    digits.length < 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
};
const toggleCalendar = () => {
  if (isOpen.value) {
    isOpen.value = false;
    return;
  }

  setTimeout(() => {
    isOpen.value = true;
  });
};
const syncFromValue = value => {
  const date = parseLocalDateTime(value);
  selectedDate.value = date;
  viewedMonth.value = startOfMonth(date || new Date());
  hour.value = pad(date?.getHours() ?? 9);
  minute.value = pad(date?.getMinutes() ?? 0);
  timeInput.value = `${hour.value}:${minute.value}`;
};
watch(() => props.modelValue, syncFromValue, { immediate: true });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="scheduled-date-time-picker flex items-center gap-2">
    <div class="relative">
      <button
        type="button"
        :disabled="disabled"
        class="flex h-[38px] w-44 items-center gap-2 rounded-lg bg-n-alpha-2 px-3 text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-1"
        @click="toggleCalendar"
      >
        <i class="i-lucide-calendar-range size-3.5 shrink-0 text-n-slate-11" />
        <span class="min-w-0 flex-1 truncate text-left">{{
          selectedDateLabel
        }}</span>
        <i class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-12" />
      </button>
      <div
        v-if="isOpen"
        class="absolute left-0 top-10 z-50 w-80 rounded-xl bg-n-alpha-3 p-4 shadow-md backdrop-blur-[100px] outline outline-1 outline-n-container"
        @click.stop
      >
        <div class="mb-4 flex items-center justify-between">
          <button
            type="button"
            class="grid size-7 place-items-center rounded-md hover:bg-n-alpha-1"
            :aria-label="t('DATE_PICKER.PREVIOUS_MONTH')"
            @click="viewedMonth = subMonths(viewedMonth, 1)"
          >
            <i class="i-lucide-chevron-left size-4" />
          </button>
          <p class="text-sm font-medium capitalize text-n-slate-12">
            {{ monthLabel }}
          </p>
          <button
            type="button"
            class="grid size-7 place-items-center rounded-md hover:bg-n-alpha-1"
            :aria-label="t('DATE_PICKER.NEXT_MONTH')"
            @click="viewedMonth = addMonths(viewedMonth, 1)"
          >
            <i class="i-lucide-chevron-right size-4" />
          </button>
        </div>
        <div class="grid grid-cols-7 gap-y-1 text-center text-xs">
          <span
            v-for="day in weekdays"
            :key="day"
            class="py-1 font-medium text-n-slate-11"
          >
            {{ day }}
          </span>
          <button
            v-for="date in calendarDays"
            :key="date.toISOString()"
            type="button"
            class="mx-auto grid size-8 place-items-center rounded-lg transition-colors"
            :class="[
              isSameDay(date, selectedDate)
                ? 'bg-n-brand font-medium text-white'
                : 'text-n-slate-12 hover:bg-n-alpha-1',
              !isSameMonth(date, viewedMonth) && 'text-n-slate-9',
              isDisabled(date) &&
                'cursor-not-allowed opacity-40 hover:bg-transparent',
            ]"
            :disabled="isDisabled(date)"
            @click="selectDate(date)"
          >
            {{ date.getDate() }}
          </button>
        </div>
        <div class="mt-4 flex justify-between border-t border-n-strong pt-3">
          <button
            type="button"
            class="text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
            @click="clear"
          >
            {{ t('DATE_PICKER.CLEAR_BUTTON') }}
          </button>
          <button
            type="button"
            class="text-sm font-medium text-n-brand hover:underline"
            @click="viewedMonth = startOfMonth(new Date())"
          >
            {{ t('DATE_PICKER.TODAY') }}
          </button>
        </div>
      </div>
    </div>
    <div v-if="showTime" class="relative flex h-[38px]">
      <i
        class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 i-lucide-clock size-4 text-n-slate-11"
      />
      <input
        v-model="timeInput"
        type="text"
        :disabled="disabled"
        inputmode="numeric"
        maxlength="5"
        aria-label="Horário do agendamento"
        placeholder="HH:MM"
        class="h-[38px] w-24 rounded-lg border border-n-weak bg-n-solid-2 py-2 pl-10 pr-2 text-sm font-medium text-n-slate-12 outline-none focus:border-n-brand"
        @input="formatTimeInput"
        @blur="commitTime"
        @keydown.enter.prevent="commitTime"
      />
    </div>
    <button
      v-if="selectedDate"
      type="button"
      :disabled="disabled"
      class="text-xs font-medium text-n-brand hover:underline"
      @click="clear"
    >
      {{ t('DATE_PICKER.CLEAR_BUTTON') }}
    </button>
  </div>
</template>

<style scoped>
.scheduled-date-time-picker input[placeholder='HH:MM'] {
  width: 6rem !important;
  height: 2.375rem !important;
  padding-left: 2.75rem !important;
}
</style>
